import React, { useEffect, useRef, useState } from 'react';
import './CustomDatePicker.scss';
import { FaCalendar } from "react-icons/fa6";

interface Props {
  value: string; // yyyy-mm-dd
  onChange: (date: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatISO(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseISO(value: string): Date | null {
  if (!value || typeof value !== 'string') return null;
  
  // Validate the format yyyy-mm-dd
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoDateRegex.test(value)) return null;
  
  const parts = value.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  
  const [year, month, day] = parts;
  
  // Validate ranges
  if (year < 1900 || year > 2100) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  
  const date = new Date(year, month - 1, day);
  
  // Check if the date is valid (handles cases like Feb 30th)
  if (date.getFullYear() !== year || 
      date.getMonth() !== month - 1 || 
      date.getDate() !== day) {
    return null;
  }
  
  return date;
}

function isSameDate(date1: Date, date2: Date): boolean {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
}

const weekDayShort = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];

const CustomDatePicker: React.FC<Props> = ({ value, onChange, disabled = false, placeholder = '' }) => {
  const parsed = parseISO(value);
  const [open, setOpen] = useState(false);
  // This state will now exclusively control the calendar's month/year view
  const [calendarDate, setCalendarDate] = useState(new Date()); 
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState<'left' | 'right'>('left');
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const calendarRef = useRef<HTMLDivElement | null>(null);

  // Define updateCalendarPosition function before using it
  const updateCalendarPosition = () => {
    if (!wrapperRef.current || !open) return;
    
    const rect = wrapperRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const calendarEl = calendarRef.current;
    const calendarWidth = (calendarEl?.getBoundingClientRect().width) || 220;
    const calendarHeight = (calendarEl?.getBoundingClientRect().height) || 320;
    
    // Check if calendar would overflow on the right side
    if (rect.left + calendarWidth > windowWidth - 20) {
      setCalendarPosition('right');
    } else {
      setCalendarPosition('left');
    }
    
    // Update calendar position using fixed positioning
    if (calendarEl) {
      calendarEl.style.position = 'fixed';
      // Decide vertical placement (below or above) to avoid clipping
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placeBelow = spaceBelow >= calendarHeight + 12 || spaceBelow >= spaceAbove;

      if (placeBelow) {
        calendarEl.style.top = `${rect.bottom + 6}px`;
      } else {
        calendarEl.style.top = `${Math.max(8, rect.top - calendarHeight - 6)}px`;
      }

      // Horizontal placement
      const isSmallScreen = windowWidth <= 768;
      if (isSmallScreen) {
        // Center horizontally on small screens to avoid overflow
        calendarEl.style.transform = 'none';
        const leftCentered = Math.max(16, (windowWidth - calendarWidth) / 2);
        calendarEl.style.left = `${leftCentered}px`;
        calendarEl.style.right = 'auto';
        // Ensure it doesn't overflow
        const rightEdge = leftCentered + calendarWidth;
        if (rightEdge > windowWidth - 16) {
          calendarEl.style.left = `${windowWidth - calendarWidth - 16}px`;
        }
      } else {
        // Desktop: align to input, but clamp within viewport
        calendarEl.style.transform = 'none';
        let left = rect.left;
        // If overflowing on right, prefer right anchoring
        if (left + calendarWidth > windowWidth - 8) {
          calendarEl.style.left = 'auto';
          calendarEl.style.right = `${Math.max(8, windowWidth - rect.right)}px`;
        } else {
          // Clamp to viewport
          left = Math.min(left, windowWidth - calendarWidth - 8);
          left = Math.max(8, left);
          calendarEl.style.left = `${left}px`;
          calendarEl.style.right = 'auto';
        }
      }
    }
  };

  useEffect(() => {
    // When the calendar is opened, set its view to the selected date or today's date
    if (open) {
      const initialDate = parseISO(value) ?? new Date();
      setCalendarDate(initialDate);
      
      // Update position after calendar renders
      setTimeout(() => {
        updateCalendarPosition();
      }, 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowYearSelector(false);
      }
    }

  document.addEventListener('mousedown', onDocClick);
  window.addEventListener('resize', updateCalendarPosition);
  // Reposition on scroll (captures offcanvas/body scroll as well)
  document.addEventListener('scroll', updateCalendarPosition, true);
    
    if (open) {
      // Small delay to ensure DOM is updated
      setTimeout(updateCalendarPosition, 10);
    }
    
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('resize', updateCalendarPosition);
      document.removeEventListener('scroll', updateCalendarPosition, true);
    };
  }, [open]);

  // Calendar calculation logic now uses `calendarDate`
  const firstDayOfMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
  const lastDayOfMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startWeekday = firstDayOfMonth.getDay();

  // Generate calendar days array
  const days: Array<(number | null)> = [];
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startWeekday; i++) {
    days.push(null);
  }
  
  // Add all days of the month
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(d);
  }

  const today = new Date();
  const selectedDate = parsed;

  function changeMonth(offset: number) {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
    setShowYearSelector(false);
  }

  function changeYear(year: number) {
    setCalendarDate(prev => new Date(year, prev.getMonth(), 1));
    setShowYearSelector(false);
  }

  function handleSelectDay(day: number) {
    const newDate = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
    onChange(formatISO(newDate));
    setOpen(false);
    setShowYearSelector(false);
  }

  function handleCalendarClick(e: React.MouseEvent) {
    e.stopPropagation();
  }

  function handleMonthYearClick(e: React.MouseEvent) {
    e.stopPropagation();
    setShowYearSelector(!showYearSelector);
  }

  function formatDisplayValue(dateString: string): string {
    if (!dateString) return placeholder || 'Seleccionar fecha';
    
    const date = parseISO(dateString);
    if (!date) return dateString;
    
    // Format as dd/mm/yyyy for better readability
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  }

  // Generate years array for year selector
  const currentYear = new Date().getFullYear();
  const yearRange = 10;
  const years = Array.from(
    { length: yearRange * 2 + 1 }, 
    (_, i) => currentYear - yearRange + i
  );

  return (
    <div className={`custom-datepicker-wrapper ${disabled ? 'disabled' : ''}`} ref={wrapperRef}>
      <button
        type="button"
        className="custom-date-input"
        onClick={() => !disabled && setOpen((s) => !s)}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={`date-text ${!value ? 'placeholder' : ''}`}>
          {value ? formatDisplayValue(value) : placeholder || 'Seleccionar fecha'}
        </span>
        <span className="date-icon" aria-hidden><FaCalendar /></span>
      </button>

      {open && (
        <div 
          ref={calendarRef}
          className={`custom-calendar ${calendarPosition === 'right' ? 'position-right' : 'position-left'}`} 
          role="dialog" 
          aria-label="Selector de fecha"
          onClick={handleCalendarClick}
        >
          <div className="calendar-header">
            <button 
              type="button"
              className="nav-btn" 
              onClick={(e) => {
                e.stopPropagation();
                changeMonth(-1);
              }} 
              aria-label="Mes anterior"
            >
              ‹
            </button>
            
            <button
              type="button"
              className="month-year-selector"
              onClick={handleMonthYearClick}
              aria-label="Seleccionar mes y año"
            >
              <span className="month-name">
                {calendarDate.toLocaleString('es-ES', { month: 'long' })}
              </span>
              <span className="year-number">
                {calendarDate.getFullYear()}
              </span>
              <span className="dropdown-arrow">▼</span>
            </button>
            
            <button 
              type="button"
              className="nav-btn" 
              onClick={(e) => {
                e.stopPropagation();
                changeMonth(1);
              }} 
              aria-label="Mes siguiente"
            >
              ›
            </button>
          </div>

          {showYearSelector && (
            <div className="year-selector">
              <div className="year-grid">
                {years.map((year) => (
                  <button
                    key={year}
                    type="button"
                    className={`year-option ${year === calendarDate.getFullYear() ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      changeYear(year);
                    }}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="calendar-grid">
            {weekDayShort.map((w) => (
              <div className="weekday" key={w}>{w}</div>
            ))}

            {days.map((d, idx) => {
              if (d === null) {
                return <div className="day empty" key={`empty-${idx}`} />;
              }
              
              const dayDate = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), d);
              const isToday = isSameDate(today, dayDate);
              const isSelected = selectedDate ? isSameDate(selectedDate, dayDate) : false;
              
              return (
                <button
                  key={`day-${d}-${idx}`}
                  type="button"
                  className={`day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectDay(d);
                  }}
                  aria-label={`Seleccionar ${d} de ${calendarDate.toLocaleString('es-ES', { month: 'long', year: 'numeric' })}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
